import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, KeyRound, ShieldOff, UserCheck, UserX, Building, Mail, Phone, IdCard, CalendarClock, Loader2, FileText } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function StaffDetailPage() {
  const { staffId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [suspendDlg, setSuspendDlg] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [resetDlg, setResetDlg] = useState(false);
  const [resetForm, setResetForm] = useState({ new_password: "", auto_generate: true });
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [regenDlg, setRegenDlg] = useState(false);
  const [regenForm, setRegenForm] = useState({ mode: "auto", custom_login_id: "", custom_password: "" });
  const [regenResult, setRegenResult] = useState(null);
  const [regenBusy, setRegenBusy] = useState(false);

  const downloadCombinedReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.get(`/org/staff/${staffId}/all-exams-report.pdf`, {
        responseType: "blob",
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `exam_record_${(payload?.user?.full_name || "staff").replace(/\s+/g, "_")}_${staffId.slice(0,8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Combined exam report downloaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReportBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/org/staff/${staffId}`);
      setPayload(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("reset") === "1") setResetDlg(true); }, [params]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/org/staff/${staffId}/status`, { account_status: status });
      toast.success(`Status set to ${status}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setConfirm(null);
  };

  const submitSuspend = async () => {
    if (suspendReason.trim().length < 3) return toast.error("Reason must be at least 3 characters.");
    try {
      await api.patch(`/org/staff/${staffId}/status`, { account_status: "suspended", reason: suspendReason });
      toast.success("Staff suspended.");
      setSuspendDlg(false); setSuspendReason(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitReset = async () => {
    setResetting(true); setResetResult(null);
    try {
      const body = resetForm.auto_generate ? { auto_generate: true } : { new_password: resetForm.new_password };
      const { data } = await api.post(`/org/staff/${staffId}/reset-password`, body);
      setResetResult(data.data.generated_password || "Password updated.");
      toast.success("Password reset.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResetting(false); }
  };

  const submitRegenLogin = async () => {
    setRegenBusy(true); setRegenResult(null);
    try {
      const body = regenForm.mode === "auto"
        ? {}
        : {
            custom_login_id: regenForm.custom_login_id.trim() || null,
            custom_password: regenForm.custom_password.trim() || null,
          };
      const { data } = await api.post(`/org/staff/${staffId}/regenerate-login`, body);
      setRegenResult(data.data);
      toast.success(`New login: ${data.data.login_id} / ${data.data.password}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRegenBusy(false); }
  };

  if (loading) return <DashboardLayout title="Staff profile"><LoadingBlock label="Loading staff…" /></DashboardLayout>;
  if (!payload) return null;
  const u = payload.user;

  return (
    <DashboardLayout title={u.full_name} breadcrumbs={["My Institute", "Staff", u.full_name]}>
      <div className="space-y-6" data-testid={ADMIN.staffDetailRoot}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/app/hospital/staff" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Staff
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/app/hospital/staff/${staffId}/edit`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="staff-detail-edit">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Link>
            <button onClick={downloadCombinedReport} disabled={reportBusy}
              className="inline-flex items-center gap-1.5 rounded-md border-2 border-[#329112] bg-[#329112]/5 px-3 py-1.5 text-xs font-bold text-[#256b0d] hover:bg-[#329112]/10 disabled:opacity-60"
              data-testid="staff-detail-combined-report"
              title="Download combined exam record (all topics)">
              {reportBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              Combined exam report
            </button>
            <button onClick={() => setResetDlg(true)} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="staff-detail-reset">
              <KeyRound className="h-3.5 w-3.5" /> Reset password
            </button>
            {u.role_code === "student" && (
              <button onClick={() => setRegenDlg(true)}
                className="inline-flex items-center gap-1.5 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100"
                data-testid="staff-detail-regen-login"
                title="Rotate this student's login_id + password (useful if the auto-generated ID clashed and became Dinesh17).">
                <KeyRound className="h-3.5 w-3.5" /> Regen Login
              </button>
            )}
            {u.account_status !== "active" && (
              <button onClick={() => setConfirm({ status: "active", label: "Activate", desc: "Activate staff account?" })} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="staff-detail-activate">
                <UserCheck className="h-3.5 w-3.5" /> Activate
              </button>
            )}
            {u.account_status !== "inactive" && (
              <button onClick={() => setConfirm({ status: "inactive", label: "Deactivate", desc: "Deactivate staff account? They will not be able to log in." })} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                <UserX className="h-3.5 w-3.5" /> Deactivate
              </button>
            )}
            {u.account_status !== "suspended" && (
              <button onClick={() => setSuspendDlg(true)} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100" data-testid="staff-detail-suspend">
                <ShieldOff className="h-3.5 w-3.5" /> Suspend
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#101876] text-lg font-bold text-white">
                  {(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}
                </div>
                <div className="min-w-0">
                  <div className="text-xl font-bold text-slate-900">{u.full_name}</div>
                  <div className="text-sm text-slate-500">{ROLE_LABELS[u.role_code] || u.role_code} · {u.designation || "—"}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <StatusBadge status={u.account_status} />
                    {u.employee_id && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 font-mono">{u.employee_id}</span>}
                    {u.login_created && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Login enabled</span>}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2 text-sm text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {u.email}</div>
                <div className="flex items-center gap-2 text-sm text-slate-700"><Phone className="h-4 w-4 text-slate-400" /> {u.mobile}</div>
                {u.login_id && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2" data-testid="staff-detail-login-id">
                    <KeyRound className="h-4 w-4 text-slate-400" />
                    <span>Login ID:</span>
                    <span className="rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-xs font-bold text-emerald-800">{u.login_id}</span>
                    <button type="button"
                      onClick={() => { navigator.clipboard?.writeText(u.login_id); toast.success("Login ID copied"); }}
                      className="text-[11px] font-semibold text-[#101876] hover:underline"
                      data-testid="staff-detail-login-id-copy">Copy</button>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-700"><Building className="h-4 w-4 text-slate-400" /> {u.department_name || "No department"}</div>
                <div className="flex items-center gap-2 text-sm text-slate-700"><IdCard className="h-4 w-4 text-slate-400" /> {u.designation || "No designation"}</div>
                <div className="flex items-center gap-2 text-sm text-slate-700"><CalendarClock className="h-4 w-4 text-slate-400" /> Joined {u.joining_date || "—"}</div>
                <div className="flex items-center gap-2 text-sm text-slate-700"><UserCheck className="h-4 w-4 text-slate-400" /> Reports to {u.reporting_manager?.full_name || "—"}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Audit trail</h3>
              <div className="mt-3 space-y-2">
                {(payload.audit_logs || []).length === 0 && <div className="text-xs text-slate-500">No audit events yet.</div>}
                {(payload.audit_logs || []).slice(0, 20).map((a, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-md border border-slate-100 bg-slate-50/60 p-3">
                    <div className="mt-0.5 h-2 w-2 rounded-full bg-[#329112]" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800">{a.action}</div>
                      <div className="text-xs text-slate-600">{a.description}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{a.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Meta</h3>
              <dl className="mt-3 space-y-2 text-xs">
                <div className="flex justify-between"><dt className="text-slate-500">User ID</dt><dd className="font-mono text-slate-700 truncate">{u.id}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Created</dt><dd className="text-slate-700">{u.created_at?.slice(0, 10)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Updated</dt><dd className="text-slate-700">{u.updated_at?.slice(0, 10)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Email verified</dt><dd className="text-slate-700">{u.email_verified ? "Yes" : "No"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Login created</dt><dd className="text-slate-700">{u.login_created ? "Yes" : "No"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Last login</dt><dd className="text-slate-700">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "Never"}</dd></div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.desc}
        description="Confirm this action."
        confirmLabel={confirm?.label}
        destructive={confirm?.status === "inactive"}
        onConfirm={() => changeStatus(confirm.status)}
        testKey="staff-detail-status-confirm"
      />

      <Dialog open={suspendDlg} onOpenChange={setSuspendDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Suspend staff</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea rows={3} value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} data-testid="staff-detail-suspend-reason" />
          </div>
          <DialogFooter>
            <button onClick={() => setSuspendDlg(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitSuspend} className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700" data-testid="staff-detail-suspend-submit">Suspend</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDlg} onOpenChange={(o) => { setResetDlg(o); if (!o) { setResetResult(null); setResetForm({ new_password: "", auto_generate: true }); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
          {!resetResult ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Auto-generate</div>
                  <div className="text-xs text-slate-500">Create a strong random password.</div>
                </div>
                <Switch checked={resetForm.auto_generate} onCheckedChange={(v) => setResetForm({ ...resetForm, auto_generate: v })} data-testid="staff-reset-auto" />
              </div>
              {!resetForm.auto_generate && (
                <div className="space-y-1">
                  <Label>New password *</Label>
                  <Input value={resetForm.new_password} onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })} placeholder="Min 8 chars" data-testid="staff-reset-newpw" />
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">New password</div>
              <div className="mt-1 font-mono text-base text-emerald-900">{resetResult}</div>
              <div className="mt-1 text-xs text-emerald-700">Copy and share securely — this is shown only once.</div>
            </div>
          )}
          <DialogFooter>
            {!resetResult ? (
              <>
                <button onClick={() => setResetDlg(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button onClick={submitReset} disabled={resetting} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="staff-reset-submit">
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reset
                </button>
              </>
            ) : (
              <button onClick={() => setResetDlg(false)} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Close</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Login (students only) */}
      <Dialog open={regenDlg} onOpenChange={(o) => { setRegenDlg(o); if (!o) { setRegenResult(null); setRegenForm({ mode: "auto", custom_login_id: "", custom_password: "" }); } }}>
        <DialogContent data-testid="regen-login-dialog">
          <DialogHeader><DialogTitle>Regenerate student login</DialogTitle></DialogHeader>
          {!regenResult ? (
            <div className="space-y-3">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                Ye student ke login_id + password ko dono rotate karega. Purani credentials turant kaam karna band ho jaayengi.
                Current: <b className="font-mono">{u.login_id || "—"}</b>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="regen-mode" checked={regenForm.mode === "auto"}
                  onCheckedChange={(v) => setRegenForm((p) => ({ ...p, mode: v ? "auto" : "custom" }))}
                  data-testid="regen-mode-toggle" />
                <Label htmlFor="regen-mode" className="text-xs font-semibold text-slate-700">
                  Auto from first name ({u.first_name})
                </Label>
              </div>
              {regenForm.mode === "custom" && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Custom Login ID (letters, digits, _, -)</Label>
                    <Input value={regenForm.custom_login_id}
                      onChange={(e) => setRegenForm((p) => ({ ...p, custom_login_id: e.target.value }))}
                      placeholder={`e.g. ${u.first_name}42`}
                      data-testid="regen-custom-login-id" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Custom Password (blank = same as Login ID)</Label>
                    <Input value={regenForm.custom_password}
                      onChange={(e) => setRegenForm((p) => ({ ...p, custom_password: e.target.value }))}
                      placeholder="Optional"
                      data-testid="regen-custom-password" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-emerald-900">
              <div className="text-xs font-bold uppercase tracking-widest">New Credentials · shown once</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-emerald-700">Login ID:</span></div>
                <div className="font-mono font-bold">{regenResult.login_id}</div>
                <div><span className="text-emerald-700">Password:</span></div>
                <div className="font-mono font-bold">{regenResult.password}</div>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(`Login ID: ${regenResult.login_id}\nPassword: ${regenResult.password}`); toast.success("Copied"); }}
                className="mt-3 rounded-md bg-white px-3 py-1 text-xs font-bold text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
                data-testid="regen-copy">Copy both</button>
            </div>
          )}
          <DialogFooter>
            {!regenResult ? (
              <>
                <button onClick={() => setRegenDlg(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button onClick={submitRegenLogin} disabled={regenBusy}
                  className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-70"
                  data-testid="regen-login-submit">
                  {regenBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Regenerate
                </button>
              </>
            ) : (
              <button onClick={() => setRegenDlg(false)} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Close</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
